import moment from "moment";
import { paymentTypeOptionsEnum } from "src/common_constants/EnumsConstants/CommonEnums";
import { allFuelLevelOptions } from "src/common_constants/Options/BasicOptions";

export const transformAttributes = (attributes: any[]) => {
  return attributes?.reduce((acc, attr) => {
    const key = attr.attributePlace || "default";
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(attr);
    return acc;
  }, {} as Record<string, any[]>);
};

export const formats = (formatName: string, data: string) => {
  if (!data) return null;
  switch (formatName) {
    case "date":
      return moment(data).format("YYYY-MM-DD");

    default:
      return formatName;
  }
};

export const generateFullAddress = (customerAddress: any = {}) => {
  const {
    street = "",
    city = "",
    stateName = "",
    postalCode = "",
    countryName = "",
  } = customerAddress;

  let fullAddress = `-`;
  if (street) {
    fullAddress = `${street}`;
  }
  if (city) {
    fullAddress = `${fullAddress}, ${city}`;
  }
  if (stateName) {
    fullAddress += `, ${stateName}`;
  }
  if (postalCode) {
    fullAddress += `, ${postalCode}`;
  }

  if (countryName) {
    fullAddress += `, ${countryName}`;
  }

  return fullAddress;
};

export const hasTime = (date: moment.Moment) => {
  return date.hours() !== 0 || date.minutes() !== 0 || date.seconds() !== 0;
};

export const normalizePhoneNumber = (phone: string): string => {
  phone = phone.replace(/^\+1/, "");

  return phone.replace(/\D/g, "");
};

export const legacyPhoneRegex = /^\+?[0-9]\d{0,19}$/;
export const formattedPhoneRegex = /^\d{3}-\d{3}-\d{4}$/;
export const phoneNumberRegex =
  /^(\+?[0-9]\d{0,19}|\d{3}-\d{3}-\d{4})$/;

export const isPhoneFieldName = (fieldName: string) =>
  fieldName.toLowerCase().includes("phone");

export const formatPhoneNumber = (value: string | null | undefined) => {
  if (!value) return "";

  let digits = String(value).replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }

  digits = digits.slice(0, 10);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const normalizeLegacyPhoneInput = (value: string | null | undefined) => {
  if (!value) return "";

  const str = String(value);
  const prefix = str.trim().startsWith("+") ? "+" : "";
  return `${prefix}${str.replace(/\D/g, "").slice(0, 20)}`;
};

export const isPhoneLikeInput = (value: string | null | undefined) => {
  const str = String(value ?? "");
  return /\d/.test(str) && /^[\d+().\-\s]*$/.test(str);
};

export const formatPhoneSearchInput = (
  value: string | null | undefined,
  hasPhoneFormat: boolean,
) => {
  if (!isPhoneLikeInput(value)) {
    return String(value ?? "");
  }

  return hasPhoneFormat
    ? formatPhoneNumber(value)
    : normalizeLegacyPhoneInput(value);
};

export const getPhoneSearchQuery = (value: string | null | undefined) => {
  if (!isPhoneLikeInput(value)) {
    return String(value ?? "");
  }

  return normalizePhoneNumber(String(value ?? ""));
};

export const formatPhoneNumberForDisplay = (
  value: string | null | undefined,
  hasPhoneFormat: boolean,
) => {
  if (!value) return "-";

  return hasPhoneFormat ? formatPhoneNumber(value) : String(value);
};

export const getNestedObjectValue = (obj: any, path: string) =>
  path.split(".").reduce((acc, key) => acc?.[key], obj);

export const findCountryByName = (countryName: string, countries: any[]) => {
  if (!countryName) return null;
  const countryMatch = countries.find(
    (country) =>
      country.name.toLowerCase() === countryName.toLowerCase() ||
      country?.code?.toLowerCase() === countryName.toLowerCase() ||
      country?.shortCode?.toLowerCase() === countryName.toLowerCase()
  );
  return countryMatch || null;
};

export const findStateByName = (stateName: string, statesList: any[]) => {
  if (!stateName) return null;
  const stateMatch = statesList.find(
    (state) =>
      state.name.trim().toLowerCase() === stateName.toLowerCase() ||
      state.code.trim().toLowerCase() === stateName.toLowerCase()
  );
  return stateMatch || null;
};

export function getFuelLevelOptionsDynamic(fuelLevelFormat: string) {
  let formatSteps: number;
  switch (fuelLevelFormat) {
    case "Fourths":
      formatSteps = 4;
      break;
    case "Eighths":
      formatSteps = 8;
      break;
    default:
      formatSteps = 4;
  }
  const stepIncrement = 8 / formatSteps;

  let details = allFuelLevelOptions.filter(
    (level) => level.step % stepIncrement === 0
  );

  return details;
}

export function pctToFuelLevel(
  pct: number | null | undefined,
  fuelLevelFormat: string,
): string | null {
  if (pct == null) return null;
  const options = getFuelLevelOptionsDynamic(fuelLevelFormat);
  const stepValue = (pct / 100) * 8;
  return options.reduce((prev, curr) =>
    Math.abs(curr.step - stepValue) < Math.abs(prev.step - stepValue)
      ? curr
      : prev,
  ).value;
}

export const getPaymentTypeOptions = (
  allOptions: any,
  context: "add" | "payment" | "edit"
): { label: string; value: string }[] => {
  switch (context) {
    case "payment":
      return allOptions.filter(
        (opt: any) => opt.value !== paymentTypeOptionsEnum.Payment
      );
    case "add":
      return allOptions.filter(
        (opt: any) => opt.value !== paymentTypeOptionsEnum.AdvancePayment
      );
    case "edit":
    default:
      return allOptions;
  }
};
